// Function index:
//   composeEncounter(cast, venue) — pure DOM builder for the battle-line
//       diorama: backdrop svg + party/foe figure rows (hp bar, name,
//       mirror-facing foes, horse+rider composition, wagon prop, ≤6 loose /
//       ≤10 capped with a +N chip) + a hidden chevron slot the window
//       positions. No listeners, no state — the window owns behavior.

import { BACKDROPS, type VenueKind } from './parts/backdrops.js';
import { BODY_PARTS } from './parts/bodies.js';
import { CREATURE_PARTS } from './parts/creatures.js';
import { WEAPON_PARTS } from './parts/weapons.js';
import { HORSE, WAGON } from './parts/props.js';
import type { EncounterCast, EncounterFigure } from './types.js';

const SVGNS = 'http://www.w3.org/2000/svg';
const FULL_SIZE_MAX = 6;
const HARD_CAP = 10;

function artSvg(inner: string, width: number, extraClass = ''): SVGSVGElement {
  const svg = document.createElementNS(SVGNS, 'svg');
  svg.setAttribute('viewBox', `0 0 ${width} 60`);
  svg.setAttribute('class', `ew-art${extraClass ? ` ${extraClass}` : ''}`);
  svg.innerHTML = inner;
  return svg;
}

function figureArt(f: EncounterFigure): SVGSVGElement {
  const mirror = f.side === 'foe' ? 'ew-mirror' : '';
  if (f.kind === 'npc') {
    const part = CREATURE_PARTS[f.creature ?? 'humanoid'];
    // Only creatures with a declared hand get gear drawn — a wolf never has a
    // sword floated next to it, whatever the cast carries.
    const anchor = part.weaponAnchor;
    const weapon = anchor && f.weapon ? WEAPON_PARTS[f.weapon] : null;
    const shield = anchor && f.shield ? WEAPON_PARTS.shield : null;
    return artSvg(
      `<g data-part="creature">${part.svg}</g>` +
      (weapon && anchor ? `<g data-part="weapon" transform="translate(${anchor.x} ${anchor.y})">${weapon.svg}</g>` : '') +
      (shield && anchor ? `<g data-part="shield" transform="translate(${part.width - anchor.x} ${anchor.y + 4})">${shield.svg}</g>` : ''),
      part.width, mirror,
    );
  }
  const body = BODY_PARTS[f.body ?? 'generic'];
  const weapon = f.weapon ? WEAPON_PARTS[f.weapon] : null;
  const shield = f.shield ? WEAPON_PARTS.shield : null;
  const rider =
    `<g data-part="body">${body.svg}</g>` +
    (weapon ? `<g data-part="weapon" transform="translate(${body.weaponAnchor.x} ${body.weaponAnchor.y})">${weapon.svg}</g>` : '') +
    (shield ? `<g data-part="shield" transform="translate(${body.width - body.weaponAnchor.x} ${body.weaponAnchor.y + 4})">${shield.svg}</g>` : '');
  if (!f.mounted) return artSvg(rider, body.width, mirror);
  const scale = 0.8;
  const riderX = HORSE.saddle.x - (body.width * scale) / 2;
  const riderY = HORSE.saddle.y - 60 * scale;
  return artSvg(
    `<g data-part="horse">${HORSE.svg}</g>` +
    `<g data-part="rider" transform="translate(${riderX} ${riderY}) scale(${scale})">${rider}</g>`,
    HORSE.width, mirror,
  );
}

function figureEl(f: EncounterFigure): HTMLElement {
  const el = document.createElement('div');
  el.className = `ew-figure${f.defeated ? ' ew-down' : ''}`;
  el.dataset.key = f.key;
  el.appendChild(figureArt(f));
  const hp = document.createElement('div');
  hp.className = 'ew-hp';
  const fill = document.createElement('div');
  fill.className = 'ew-hp-fill';
  fill.style.width = `${f.max_hp > 0 ? Math.round((f.hp / f.max_hp) * 100) : 0}%`;
  hp.appendChild(fill);
  el.appendChild(hp);
  const name = document.createElement('div');
  name.className = 'ew-name';
  name.textContent = f.name;
  el.appendChild(name);
  return el;
}

function lineEl(side: 'party' | 'foe', figures: EncounterFigure[], wagon: boolean): HTMLElement {
  const row = document.createElement('div');
  row.className = `ew-line ew-line-${side}${figures.length > FULL_SIZE_MAX ? ' ew-tight' : ''}`;
  if (side === 'party' && wagon) {
    const w = document.createElement('div');
    w.className = 'ew-figure ew-wagon';
    w.appendChild(artSvg(`<g data-part="wagon">${WAGON.svg}</g>`, WAGON.width));
    row.appendChild(w);
  }
  for (const f of figures.slice(0, HARD_CAP)) row.appendChild(figureEl(f));
  if (figures.length > HARD_CAP) {
    const more = document.createElement('div');
    more.className = 'ew-more';
    more.textContent = `+${figures.length - HARD_CAP}`;
    row.appendChild(more);
  }
  return row;
}

export function composeEncounter(cast: EncounterCast, venue: VenueKind): HTMLElement {
  const stage = document.createElement('div');
  stage.className = 'ew-stage';
  const backdrop = document.createElementNS(SVGNS, 'svg');
  backdrop.setAttribute('viewBox', '0 0 420 190');
  backdrop.setAttribute('class', 'ew-backdrop');
  backdrop.setAttribute('preserveAspectRatio', 'xMidYMax slice');
  backdrop.innerHTML = BACKDROPS[venue];
  stage.appendChild(backdrop);
  stage.appendChild(lineEl('party', cast.figures.filter((f) => f.side === 'party'), cast.wagon));
  stage.appendChild(lineEl('foe', cast.figures.filter((f) => f.side === 'foe'), false));
  const chevron = document.createElement('div');
  chevron.className = 'ew-chevron';
  chevron.hidden = true;
  chevron.textContent = '▼';
  stage.appendChild(chevron);
  return stage;
}
